import React, { createContext, useContext, useState, useCallback } from "react";
import {
  getBusiness,
  updateOnboardingStep,
  listSocialAccounts,
  listContent,
  listWebsiteVariants,
} from "../api/smb.api";

const SmbOnboardingContext = createContext();

export const SmbOnboardingProvider = ({ children }) => {
  const [business, setBusiness] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [socialAccounts, setSocialAccounts] = useState([]);
  const [contentItems, setContentItems] = useState([]);
  const [websiteVariants, setWebsiteVariants] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadBusiness = useCallback(async (businessId) => {
    try {
      setIsLoading(true);
      const result = await getBusiness(businessId);
      if (result?.status && result?.data) {
        setBusiness(result.data);
        setCurrentStep(result.data.onboarding_step || 0);
      }
    } catch (err) {
      console.error("Load business error:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshSocialAccounts = useCallback(async (businessId) => {
    try {
      const result = await listSocialAccounts(businessId);
      if (result?.status && result?.data) {
        setSocialAccounts(result.data);
      }
    } catch (err) {
      console.error("Load social accounts error:", err);
    }
  }, []);

  const refreshContent = useCallback(async (businessId) => {
    try {
      const result = await listContent(businessId);
      if (result?.status && result?.data) {
        setContentItems(result.data.items || []);
      }
    } catch (err) {
      console.error("Load content error:", err);
    }
  }, []);

  const refreshWebsites = useCallback(async (businessId) => {
    try {
      const result = await listWebsiteVariants(businessId);
      if (result?.status && result?.data) {
        setWebsiteVariants(result.data);
      }
    } catch (err) {
      console.error("Load websites error:", err);
    }
  }, []);

  const goToStep = useCallback(async (step) => {
    setCurrentStep(step);
    if (business?.id) {
      await updateOnboardingStep(business.id, step);
    }
  }, [business]);

  const completeStep = useCallback(async () => {
    const nextStep = currentStep + 1;
    await goToStep(nextStep);
  }, [currentStep, goToStep]);

  return (
    <SmbOnboardingContext.Provider
      value={{
        business,
        setBusiness,
        currentStep,
        setCurrentStep,
        socialAccounts,
        contentItems,
        websiteVariants,
        isLoading,
        loadBusiness,
        refreshSocialAccounts,
        refreshContent,
        refreshWebsites,
        goToStep,
        completeStep,
      }}
    >
      {children}
    </SmbOnboardingContext.Provider>
  );
};

export const useSmbOnboarding = () => useContext(SmbOnboardingContext);
export default SmbOnboardingContext;
